/**
 * GenericMultiselectDropdown Component
 * A customizable dropdown component that allows users to select multiple options with search functionality.
 * Features:
 * - Multiple selection with checkboxes
 * - Search filtering
 * - Expandable selected items view
 * - Customizable labels and values
 * - Generic typing to handle any data type
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/_shadcn-ui/popover';
import { cn } from '@/utils/cn';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import { Loader2 } from 'lucide-react';

type GenericOption<T> = T;

interface GenericMultiselectDropdownProps<T> {
  onSearch?: (value: string) => void;
  limit?: number;
  id?: string;
  options: GenericOption<T>[];
  value: GenericOption<T>[];
  onChange: (value: GenericOption<T>[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  defaultValue?: GenericOption<T>[];
  labelKey: keyof T;
  valueKey: keyof T;
  isLoading?: boolean;
  renderOption?: (option: GenericOption<T>) => React.ReactNode;
  renderSelectedItem?: (option: GenericOption<T>, onRemove: () => void) => React.ReactNode;
  getOptionLabel?: (option: GenericOption<T>) => string;
  getOptionValue?: (option: GenericOption<T>) => string | number;
}

/**
 * GenericMultiselectDropdown Component
 * A dropdown component that allows multiple selections with search functionality for any data type
 */
function GenericMultiselectDropdown<T extends Record<string, any>>({
  onSearch,
  limit = 2,
  id,
  options = [],
  value = [],
  onChange,
  placeholder = 'Select Options',
  searchPlaceholder = 'Search...',
  defaultValue = [],
  labelKey,
  valueKey,
  isLoading = false,
  renderOption,
  renderSelectedItem,
  getOptionLabel,
  getOptionValue,
}: GenericMultiselectDropdownProps<T>) {
  // State management
  const [isOpen, setIsOpen] = useState(false); // Controls dropdown open/close state
  const [expanded, setExpanded] = useState(false); // Controls expanded view of selected items
  const [searchQuery, setSearchQuery] = useState(''); // Manages search input value
  const [internalValue, setInternalValue] = useState<GenericOption<T>[]>(defaultValue); // Internal state for selected values

  /**
   * Sync internal value with external value prop
   * Updates internal state when external value changes
   */
  useEffect(() => {
    setInternalValue(value || []);
  }, [value]);

  /**
   * Get option label for display
   */
  const getDisplayLabel = (option: GenericOption<T>): string => {
    if (getOptionLabel) {
      return getOptionLabel(option);
    }
    return String(option[labelKey] || '');
  };

  /**
   * Get option value for comparison
   */
  const getDisplayValue = (option: GenericOption<T>): string | number => {
    if (getOptionValue) {
      return getOptionValue(option);
    }
    return option[valueKey];
  };

  /**
   * Filter options based on search query
   * Returns options that match the search query (case-insensitive)
   */
  const filteredOptions = useMemo(() => {
    return (options || []).filter((option) =>
      getDisplayLabel(option).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery, getDisplayLabel]);

  /**
   * Get currently selected options
   * Returns full option objects for selected values
   */
  const selectedOptions = useMemo(() => {
    return (options || []).filter((option) =>
      (internalValue || []).some((selected) => getDisplayValue(selected) === getDisplayValue(option))
    );
  }, [options, internalValue, getDisplayValue]);

  /**
   * Handle checkbox selection/deselection
   * @param {GenericOption<T>} option - The option being toggled
   * @param {boolean} checked - Whether the option is being selected or deselected
   */
  const handleCheckboxChange = (option: GenericOption<T>, checked: boolean) => {
    const newValue = checked
      ? [...internalValue, option]
      : internalValue.filter((v) => getDisplayValue(v) !== getDisplayValue(option));
    setInternalValue(newValue);
    onChange(newValue);
  };

  /**
   * Handle removal of selected option
   * @param {GenericOption<T>} option - The option to remove
   */
  const handleRemove = (option: GenericOption<T>) => {
    const newValue = internalValue.filter((v) => getDisplayValue(v) !== getDisplayValue(option));
    setInternalValue(newValue);
    onChange(newValue);
    if (newValue.length <= 2) {
      setExpanded(false);
    }
  };

  /**
   * Default render function for selected items
   */
  const defaultRenderSelectedItem = (option: GenericOption<T>, onRemove: () => void) => (
    <div className="flex shrink-0 items-center border gap-[6px] border-[#0C8DBF] bg-[#E4F8FF] px-[4px] py-[2px] relative rounded-[4px]">
      <span className="text-[12px] flex-1 text-[#0C8DBF]">{getDisplayLabel(option)}</span>
      <div
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="hover:opacity-70"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M10.5 3.5L3.5 10.5" stroke="#0C8DBF" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3.5 3.5L10.5 10.5" stroke="#0C8DBF" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );

  /**
   * Default render function for options
   */
  const defaultRenderOption = (option: GenericOption<T>) => (
    <span className="text-[12px] font-normal hover:text-[#4B4B4B]">{getDisplayLabel(option)}</span>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {/* Main trigger button that shows selected items */}
      <PopoverTrigger className={cn('w-full')}>
        <div
          className={cn(
            'flex items-center flex-wrap relative gap-[4px] bg-white w-full px-[10px] min-h-[38px] rounded-[8px] border border-[#D9D9D9] py-[6px]',
            {
              'h-max': expanded,
            },
            {
              'border-[#0C8DBF]': isOpen,
            }
          )}
        >
          {/* Placeholder text when no items are selected */}
          {selectedOptions.length === 0 ? (
            <span className="block text-[#9D9D9C] text-[14px] font-normal absolute left-3 top-[50%] translate-y-[-50%]">
              {placeholder}
            </span>
          ) : null}

          {/* Container for selected items */}
          <div className="flex flex-wrap gap-[4px] w-[calc(100%-24px)]">
            {/* Expanded view of selected items */}
            {expanded
              ? selectedOptions.map((option) => (
                  <div key={String(getDisplayValue(option))}>
                    {renderSelectedItem
                      ? renderSelectedItem(option, () => handleRemove(option))
                      : defaultRenderSelectedItem(option, () => handleRemove(option))}
                  </div>
                ))
              : /* Collapsed view showing only first 2 items */
                selectedOptions
                  .slice(0, limit)
                  .map((option) => (
                    <div key={String(getDisplayValue(option))}>
                      {renderSelectedItem
                        ? renderSelectedItem(option, () => handleRemove(option))
                        : defaultRenderSelectedItem(option, () => handleRemove(option))}
                    </div>
                  ))}
            {/* Show more/less button when more than 2 items are selected */}
            {selectedOptions.length > limit && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setExpanded(!expanded);
                }}
                className="flex items-center pl-[10px] relative rounded-[4px]"
              >
                <span className="text-[12px] text-[#0C8DBF] flex items-center gap-[4px]">
                  {expanded ? <>Show Less</> : <>Show All</>}
                </span>
              </div>
            )}
          </div>
          {/* Dropdown arrow icon */}
          <svg
            className={cn(
              'w-4 h-4 absolute right-[12px] top-[50%] translate-y-[-50%] transition-transform duration-300',
              isOpen ? 'rotate-180 fill-[#0C8DBF]' : 'fill-[#9D9D9C]'
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path d="M10.0151 4.76531L6.26512 8.51531C6.23029 8.55018 6.18893 8.57784 6.14341 8.59671C6.09789 8.61558 6.04909 8.62529 5.99981 8.62529C5.95053 8.62529 5.90173 8.61558 5.8562 8.59671C5.81068 8.57784 5.76932 8.55018 5.73449 8.51531L1.98449 4.76531C1.93199 4.71287 1.89623 4.64602 1.88173 4.57324C1.86724 4.50046 1.87467 4.42501 1.90307 4.35645C1.93148 4.28789 1.97959 4.22931 2.04131 4.1881C2.10304 4.1469 2.1756 4.12494 2.24981 4.125H9.74981C9.82402 4.12494 9.89658 4.1469 9.9583 4.1881C10.02 4.22931 10.0681 4.28789 10.0965 4.35645C10.1249 4.42501 10.1324 4.50046 10.1179 4.57324C10.1034 4.64602 10.0676 4.71287 10.0151 4.76531Z" />
          </svg>
        </div>
      </PopoverTrigger>
      {/* Dropdown content */}
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 overflow-hidden shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] rounded-[8px] border border-[#D9D9D9]">
        <div className="flex flex-col gap-2">
          {/* Search section */}
          <div className="px-[16px] pt-[8px] space-y-[4px]">
            <label htmlFor={`generic-search-${id}`} className="text-[12px] leading-[14px] font-normal text-[#9D9D9C]">
              {placeholder}
            </label>
            <div className="relative">
              {/* Search input */}
              <input
                id={`generic-search-${id}`}
                type="text"
                className="h-[38px] w-full peer px-[12px] pl-[36px] text-[#4B4B4B] text-[14px] border border-[#D9D9D9] rounded-[8px] outline-hidden focus:border-[#0C8DBF]"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  onSearch?.(value);
                }}
              />
              {/* Search icon */}
              <svg
                className="absolute left-[12px] top-[50%] translate-y-[-50%] peer-focus:stroke-[#0C8DBF] stroke-[#9D9D9C]"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
              >
                <g clipPath="url(#clip0_4876_25222)">
                  <path
                    d="M6.41667 11.0833C8.994 11.0833 11.0833 8.994 11.0833 6.41667C11.0833 3.83934 8.994 1.75 6.41667 1.75C3.83934 1.75 1.75 3.83934 1.75 6.41667C1.75 8.994 3.83934 11.0833 6.41667 11.0833Z"
                    stroke="#25151E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.25 12.25L9.7417 9.7417"
                    stroke="#25151E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4876_25222">
                    <rect width="14" height="14" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
          {/* Options list */}
          <div className="flex flex-col max-h-[200px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full py-[16px]">
                <Loader2 className="w-[32px] h-[32px] animate-spin text-[#0C8DBF]" />
              </div>
            ) : null}
            {!isLoading ? (
              filteredOptions.map((option) => (
                <div
                  key={String(getDisplayValue(option))}
                  className={cn(
                    'flex items-center gap-[8px] p-2 hover:bg-[#E4F8FF] group hover:text-[#000000] cursor-pointer',
                    {
                      'bg-[#E4F8FF] text-[#000000]': internalValue.some(
                        (selected) => getDisplayValue(selected) === getDisplayValue(option)
                      ),
                    }
                  )}
                  onClick={() =>
                    handleCheckboxChange(
                      option,
                      !internalValue.some((selected) => getDisplayValue(selected) === getDisplayValue(option))
                    )
                  }
                >
                  {/* Checkbox for each option */}
                  <Checkbox
                    className="data-[state=checked]:bg-[#0C8DBF] data-[state=checked]:text-white data-[state=checked]:border-white border-[#0C8DBF] hover:bg-white hover:text-[#0C8DBF] group-hover:bg-white group-hover:text-[#0C8DBF]"
                    checked={internalValue.some((selected) => getDisplayValue(selected) === getDisplayValue(option))}
                    onCheckedChange={(checked) => {
                      handleCheckboxChange(option, checked as boolean);
                    }}
                  />
                  {/* Option content */}
                  {renderOption ? renderOption(option) : defaultRenderOption(option)}
                </div>
              ))
            ) : filteredOptions.length === 0 ? (
              <div className="flex items-center justify-center h-full py-[16px]">
                <span className="text-[14px] text-[#9D9D9C]">No options found</span>
              </div>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default GenericMultiselectDropdown;
